import React, { useEffect, useRef, useCallback } from 'react';
import './HeroSection.css';
import { motion, useScroll, useTransform } from 'framer-motion';
import Typewriter from 'typewriter-effect';

function MagneticIcon({ href, icon, label }) {
  const ref = useRef(null);

  const onMouseMove = useCallback((e) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = e.clientX - rect.left - rect.width / 2;
    const y = e.clientY - rect.top - rect.height / 2;
    el.style.transform = `translate(${x * 0.35}px, ${y * 0.35}px)`;
    el.style.transition = 'transform 0.15s ease-out';
  }, []);

  const onMouseLeave = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.style.transform = 'translate(0, 0)';
    el.style.transition = 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)';
  }, []);

  return (
    <a
      ref={ref}
      href={href}
      target="_blank"
      rel="noreferrer"
      className="hero__social-link"
      aria-label={label}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
    >
      <i className={icon} />
    </a>
  );
}

function HeroSection() {
  const canvasRef = useRef(null);
  const sectionRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start start', 'end start'],
  });

  const bgY = useTransform(scrollYProgress, [0, 1], ['0%', '30%']);
  const particlesY = useTransform(scrollYProgress, [0, 1], ['0%', '20%']);
  const contentY = useTransform(scrollYProgress, [0, 1], ['0%', '10%']);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animationId;
    let particles = [];

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    class Particle {
      constructor() {
        this.reset();
      }
      reset() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.size = Math.random() * 1.5 + 0.5;
        this.speedX = (Math.random() - 0.5) * 0.3;
        this.speedY = (Math.random() - 0.5) * 0.3;
        this.opacity = Math.random() * 0.5 + 0.1;
      }
      update() {
        this.x += this.speedX;
        this.y += this.speedY;
        if (this.x < 0 || this.x > canvas.width) this.speedX *= -1;
        if (this.y < 0 || this.y > canvas.height) this.speedY *= -1;
      }
      draw() {
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(34, 197, 94, ${this.opacity})`;
        ctx.fill();
      }
    }

    const count = Math.min(80, Math.floor((canvas.width * canvas.height) / 15000));
    for (let i = 0; i < count; i++) particles.push(new Particle());

    const drawLines = () => {
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 120) {
            ctx.beginPath();
            ctx.strokeStyle = `rgba(34, 197, 94, ${0.06 * (1 - dist / 120)})`;
            ctx.lineWidth = 0.5;
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      }
    };

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach(p => { p.update(); p.draw(); });
      drawLines();
      animationId = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  const containerVariants = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.15, delayChildren: 0.3 } },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.4, 0, 0.2, 1] } },
  };

  const helloWorlds = [
    'print("Hello World")',
    'std::cout << "Hello World";',
    'console.log("Hello World");',
    'println("Hello World");',
    'echo "Hello World"',
  ];

  const socials = [
    { href: 'https://github.com/soum-sr', icon: 'fa-brands fa-github', label: 'GitHub' },
    { href: 'https://www.linkedin.com/in/soumyajitrout/', icon: 'fa-brands fa-linkedin', label: 'LinkedIn' },
    { href: 'https://stackoverflow.com/users/11289239/soumyajit', icon: 'fa-brands fa-stack-overflow', label: 'Stack Overflow' },
    { href: 'https://medium.com/@soum1071', icon: 'fa-brands fa-medium', label: 'Medium' },
    { href: 'mailto:soum1071@gmail.com', icon: 'fa-solid fa-envelope', label: 'Email' },
  ];

  return (
    <section id="home" className="hero" ref={sectionRef}>
      <motion.img
        src="/images/background2.svg"
        alt=""
        className="hero__bg-svg"
        style={{ y: bgY }}
      />
      <motion.canvas
        ref={canvasRef}
        className="hero__particles"
        style={{ y: particlesY }}
      />
      <div className="hero__gradient" />

      <motion.div
        className="hero__content"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        style={{ y: contentY, opacity: contentOpacity }}
      >
        <motion.div className="hero__code" variants={itemVariants}>
          <Typewriter
            options={{
              strings: helloWorlds,
              autoStart: true,
              loop: true,
              pauseFor: 2000,
              delay: 50,
              deleteSpeed: 30,
            }}
          />
        </motion.div>

        <motion.h1 className="hero__name" variants={itemVariants}>
          I'm <span className="hero__accent">Soumyajit Rout</span>
        </motion.h1>

        <motion.p className="hero__bio" variants={itemVariants}>
          Software Engineer from Bengaluru building scalable distributed systems, network automation, and AI-powered infrastructure. 
          I design reliable backend platforms that improve observability, automation, and engineering efficiency.
        </motion.p>

        <motion.div className="hero__socials" variants={itemVariants}>
          {socials.map((s) => (
            <MagneticIcon key={s.label} {...s} />
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
}

export default HeroSection;
